import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  ArrowRight,
  MoreHorizontal,
  Newspaper,
  Search,
  Sparkles,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { useStartAsset } from "~/components/nova/use-start-asset";
import { Button } from "~/components/ui/button";
import { ConfirmDialog } from "~/components/ui/confirm-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { EmptyState } from "~/components/ui/empty-state";
import { PageHeader } from "~/components/ui/page-title";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { cn } from "~/lib/cn";
import {
  archiveMyArticleFn,
  myArticlesQueryOptions,
  type ArticleRow,
} from "~/lib/functions/cih/articles";
import type { ContentStatus } from "~/lib/shared/content-status";

import { ArticleStatusBadge } from "../-components/article-status-badge";

export const Route = createFileRoute("/_user/my-coaching/articles/")({
  staticData: { title: "Articles" },
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(myArticlesQueryOptions());
    return {};
  },
  component: MyArticlesPage,
});

type StatusFilter = "all" | ContentStatus;

const STATUS_TABS: { label: string; value: StatusFilter }[] = [
  { label: "All", value: "all" },
  { label: "Draft", value: "draft" },
  { label: "In review", value: "in_review" },
  { label: "Published", value: "published" },
  { label: "Archived", value: "archived" },
];

function MyArticlesPage() {
  const [activeFilter, setActiveFilter] = useState<StatusFilter>("all");
  const { data: articles, isLoading } = useQuery(myArticlesQueryOptions());
  const queryClient = useQueryClient();

  // The archive/discard mutation and its confirm dialog live here (not per row)
  // so the whole table shares a single instance — only the targeted article id
  // is tracked in state.
  const [articleToArchive, setArticleToArchive] = useState<ArticleRow | null>(
    null,
  );

  const archiveMutation = useMutation({
    mutationFn: (id: string) => archiveMyArticleFn({ data: { id } }),
    onSuccess: (result) => {
      toast.success(result.discarded ? "Edits discarded" : "Draft archived");
      setArticleToArchive(null);
      void queryClient.invalidateQueries({
        queryKey: myArticlesQueryOptions().queryKey,
      });
    },
    onError: (err) => {
      console.error("Failed to archive article:", err);
      toast.error("Failed to archive article");
    },
  });

  const archiveCopyForActive = articleToArchive
    ? archiveCopy(articleToArchive.draftOf != null)
    : null;

  const filtered =
    activeFilter === "all"
      ? (articles ?? [])
      : (articles ?? []).filter((a) => a.status === activeFilter);

  return (
    <div className="space-y-8">
      <PageHeader
        actions={
          <div className="flex flex-col items-end gap-2">
            <DraftWithNovaButton>
              <Sparkles className="mr-2 h-4 w-4" />
              Draft with NOVA
            </DraftWithNovaButton>
            <ManualWriteLink />
          </div>
        }
        description="Manage your coaching articles for the Coaching Intelligence Hub."
        title="My articles"
      />

      {/* NOVA is the primary way to write articles. */}
      <DraftWithNovaCallout />

      {/* Status filter tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => setActiveFilter(tab.value)}
            className={cn(
              "px-4 py-2 text-sm transition-colors",
              activeFilter === tab.value
                ? "border-b-2 border-brand-600 font-medium text-brand-600"
                : "text-gray-500 hover:text-gray-700",
            )}
          >
            {tab.label}
            {tab.value !== "all" && (
              <span className="ml-1.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
                {(articles ?? []).filter((a) => a.status === tab.value).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {isLoading ? (
        <ArticlesListSkeleton />
      ) : filtered.length === 0 ? (
        activeFilter === "all" ? (
          <EmptyState
            icon={Newspaper}
            title="No articles yet"
            description="Draft your first article with NOVA to share your expertise."
          >
            <div className="flex flex-col items-center gap-2">
              <DraftWithNovaButton size="sm">
                <Sparkles className="mr-2 h-4 w-4" />
                Draft with NOVA
              </DraftWithNovaButton>
              <ManualWriteLink />
            </div>
          </EmptyState>
        ) : (
          <EmptyState
            icon={Search}
            size="sm"
            title="No matches"
            description="Try adjusting your filters."
          />
        )
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead className="w-12">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((article) => (
              <ArticleTableRow
                key={article.id}
                article={article}
                onArchive={setArticleToArchive}
              />
            ))}
          </TableBody>
        </Table>
      )}

      {archiveCopyForActive && (
        <ConfirmDialog
          open={articleToArchive != null}
          onOpenChange={(open) => {
            if (!open) setArticleToArchive(null);
          }}
          title={archiveCopyForActive.title}
          description={archiveCopyForActive.description}
          confirmLabel={archiveCopyForActive.confirmLabel}
          loadingLabel={archiveCopyForActive.loadingLabel}
          variant="destructive"
          onConfirm={() => {
            if (articleToArchive) archiveMutation.mutate(articleToArchive.id);
          }}
          isLoading={archiveMutation.isPending}
        />
      )}
    </div>
  );
}

/**
 * Starts the Articles asset: a NOVA session already working on an article.
 * When her plan does not open articles, My content explains why and offers
 * her way in.
 */
function DraftWithNovaButton({
  size,
  className,
  children,
}: {
  size?: "sm";
  className?: string;
  children: React.ReactNode;
}): React.JSX.Element {
  const start = useStartAsset();
  const navigate = useNavigate();
  const refused = start.data?.kind === "refused";
  useEffect(() => {
    if (!refused) return;
    void navigate({
      to: "/my-coaching/business/nova/content",
      search: { type: "articles" },
    });
  }, [refused, navigate]);
  return (
    <Button
      className={className}
      loading={start.isPending}
      onClick={() => start.mutate({ assetKey: "articles" })}
      size={size}
      variant="primary"
    >
      {children}
    </Button>
  );
}

/**
 * Promotes NOVA as the primary way to write articles. Sits above the article
 * list so the NOVA path is the obvious default, while the manual editor stays
 * reachable via the subtle link in the header / empty state.
 */
function DraftWithNovaCallout() {
  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-brand-100 bg-brand-50 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-brand-100">
          <Sparkles className="size-5 text-brand-600" />
        </div>
        <div className="space-y-1">
          <h2 className="text-base font-medium text-gray-900">
            Write your next article with NOVA
          </h2>
          <p className="text-text-secondary text-sm">
            NOVA drafts a publish-ready article in your voice from any published
            challenge — the fastest way to go from idea to review.
          </p>
        </div>
      </div>
      <DraftWithNovaButton className="shrink-0">
        Draft with NOVA
        <ArrowRight className="ml-2 h-4 w-4" />
      </DraftWithNovaButton>
    </div>
  );
}

/**
 * Deliberately low-key escape hatch to the manual article editor. Kept small and
 * muted so Nova reads as the primary path without removing the manual option.
 */
function ManualWriteLink() {
  return (
    <Link
      to="/my-coaching/articles/new"
      className="text-xs text-muted-foreground underline-offset-4 transition-colors hover:text-gray-700 hover:underline"
    >
      or write one manually
    </Link>
  );
}

/**
 * Confirmation copy for archiving vs. discarding a draft. An edit-in-flight of
 * a live article (`draftOf` set) is discarded outright \u2014 the live article is
 * untouched \u2014 so the copy must not imply the work is recoverable. A standalone
 * draft is soft-archived and merely leaves the active list.
 */
function archiveCopy(isEditOfPublished: boolean) {
  return isEditOfPublished
    ? {
        menuLabel: "Discard edits",
        title: "Discard edits?",
        description:
          "Your unsaved edits to this live article will be permanently deleted. The published article stays exactly as it is.",
        confirmLabel: "Discard edits",
        loadingLabel: "Discarding...",
      }
    : {
        menuLabel: "Archive draft",
        title: "Archive draft?",
        description:
          "This draft will be moved to your archived articles and removed from your active list.",
        confirmLabel: "Archive draft",
        loadingLabel: "Archiving...",
      };
}

function ArticleTableRow({
  article,
  onArchive,
}: {
  article: ArticleRow;
  onArchive: (article: ArticleRow) => void;
}) {
  const status = article.status as ContentStatus;
  const hasFeedback = status === "draft" && article.reviewNote;
  const isEditOfPublished = article.draftOf != null;
  const isDraft = status === "draft";
  const copy = archiveCopy(isEditOfPublished);

  return (
    <>
      <TableRow>
        <TableCell>
          <Link
            to="/my-coaching/articles/$articleId"
            params={{ articleId: article.id }}
            className="font-medium text-gray-900 hover:text-brand-600"
          >
            {article.title}
          </Link>
          {isEditOfPublished && (
            <span className="ml-2 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs text-amber-800">
              Edit of live article
            </span>
          )}
        </TableCell>
        <TableCell>
          <ArticleStatusBadge status={status} />
        </TableCell>
        <TableCell className="text-text-tertiary">
          {article.updatedAt
            ? new Date(article.updatedAt).toLocaleDateString()
            : "\u2014"}
        </TableCell>
        <TableCell className="text-right">
          {isDraft && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Actions for ${article.title}`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  variant="destructive"
                  onSelect={() => onArchive(article)}
                >
                  {copy.menuLabel}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </TableCell>
      </TableRow>
      {hasFeedback && (
        <TableRow className="hover:bg-amber-50/50">
          <TableCell
            colSpan={4}
            className="bg-amber-50 py-2 text-sm text-amber-800"
          >
            <span className="font-medium">Reviewer feedback:</span>{" "}
            {article.reviewNote}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function ArticlesListSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }, (_, i) => (
        <Skeleton key={i} className="h-14 w-full rounded-xl" />
      ))}
    </div>
  );
}
